import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '../stores/userStore'
import { roleIsOneOf } from '../utils/roles'
import Login from '../views/Login.vue'

const routes = [
  {
    path: '/',
    name: 'Home',
    // Owner feedback: the public "Apolaki Intelligence" landing is removed —
    // the marketing site (https://apolaki-ph.netlify.app) is THE landing now.
    // '/' forwards straight into the funnel: logged-out visitors land on
    // /login; the guard's existing Login+isAuthed branch forwards logged-in
    // users on to /dashboard (or /consent while onboarding is incomplete).
    // No ?redirect= is attached so post-login persona routing still applies.
    redirect: { name: 'Login' }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    // Owner feedback (funnel slice A5): the old "Apolaki Intelligence" PRD
    // dashboard is retired. Home is a simple, personal starting point —
    // greeting, quick actions, and the user's saved projects.
    component: () => import('../views/HomeDashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false }
  },
  {
    path: '/admin-login',
    name: 'AdminLogin',
    component: () => import('../views/AdminLogin.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/signup',
    name: 'Signup',
    component: () => import('../views/Register.vue'),
    meta: { requiresAuth: false, publicOnly: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/Register.vue'),
    meta: { requiresAuth: false, publicOnly: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('../views/ForgotPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('../views/VerifyEmail.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/auth-callback',
    name: 'AuthCallback',
    component: () => import('../views/AuthCallback.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/consent',
    name: 'ConsentOnboarding',
    component: () => import('../views/ConsentOnboarding.vue'),
    meta: { requiresAuth: true, allowsPendingConsent: true }
  },
  {
    path: '/installations',
    name: 'Installations',
    component: () => import('../views/Installations.vue'),
    meta: { requiresAuth: true, requiredConsents: ['installation_monitoring'] }
  },
  {
    path: '/installations/:id',
    name: 'InstallationDetail',
    component: () => import('../views/InstallationDetail.vue'),
    meta: { requiresAuth: true, requiredConsents: ['installation_monitoring'] }
  },
  {
    path: '/monitoring',
    name: 'Monitoring',
    component: () => import('../views/Monitoring.vue'),
    // publicPreview: logged-out visitors see an informative explainer + login CTA
    meta: { requiresAuth: true, publicPreview: true, requiredConsents: ['installation_monitoring'] }
  },
  {
    path: '/bill-upload',
    name: 'BillUpload',
    component: () => import('../views/BillUpload.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/solar-scout',
    name: 'solar-scout',
    component: () => import('@/views/SiteCapture.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/marketplace',
    name: 'Marketplace',
    component: () => import('../views/Marketplace.vue'),
    // publicPreview: catalog browsing is read-only for guests; act-CTAs require login
    meta: { requiresAuth: true, publicPreview: true }
  },
  {
    path: '/messaging',
    name: 'Messaging',
    component: () => import('../views/Messaging.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/assessment',
    name: 'Assessment',
    component: () => import('../views/Assessment.vue'),
    // publicPreview: guests get an indicative client-side estimate; saving/matching requires login
    meta: { requiresAuth: true, publicPreview: true, requiredConsents: ['profile_account', 'location_assessment'] }
  },
  {
    path: '/finance',
    name: 'Finance',
    component: () => import('../views/Finance.vue'),
    // publicPreview: guests can use the client-side financing calculator; ledger requires login
    meta: { requiresAuth: true, publicPreview: true, requiredConsents: ['finance_data'] }
  },
  {
    path: '/contracts',
    name: 'Contracts',
    component: () => import('../views/Contracts.vue'),
    meta: { requiresAuth: true, requiredConsents: ['contracts_signing'] }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/kitchen-sink',
    redirect: '/dashboard'
  },
  {
    path: '/quotes',
    name: 'QuoteGenerator',
    component: () => import('../views/QuoteGenerator.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: ['dealer', 'supplier', 'admin', 'superadmin']
      // No requiredConsents: quotes are the dealer's own working data, not customer-shared data.
    }
  },
  {
    path: '/installer-feed',
    name: 'InstallerFeed',
    component: () => import('../views/InstallerFeed.vue'),
    // publicPreview: guests browse the anonymised public feed (public API subset)
    meta: { requiresAuth: true, publicPreview: true }
    // Any authed user browses the anonymised feed; posting is gated server-side
    // (dealer/supplier/admin) and surfaced in the UI via the user's role.
  },
  {
    path: '/installer-feed/i/:handle',
    name: 'InstallerProfile',
    component: () => import('../views/InstallerProfile.vue'),
    // publicPreview: installer marketing profiles are visitor-viewable (public API subset)
    meta: { requiresAuth: true, publicPreview: true }
  },
  {
    path: '/my-public-profile',
    name: 'MyPublicProfile',
    component: () => import('../views/MyPublicProfile.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: ['dealer', 'supplier', 'admin', 'superadmin']
      // The dealer's own marketing profile builder (handle, bio, service areas).
    }
  },
  // ── Persona Routes ─────────────────────────────────────────────
  {
    path: '/dealer',
    name: 'DealerPortal',
    component: () => import('../views/DealerPortal.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: ['dealer', 'admin', 'superadmin']
      // No requiredConsents: `partner_sharing` is the CUSTOMER's consent to share
      // their data with dealers — a dealer viewing their own portal must not need it.
    }
  },
  {
    path: '/operations',
    name: 'OperationsCenter',
    component: () => import('../views/OperationsCenter.vue'),
    meta: {
      requiresAuth: true,
      allowedRoles: ['operations', 'admin', 'superadmin'],
      requiredConsents: ['installation_monitoring', 'partner_sharing'],
      consentBypassRoles: ['admin', 'superadmin']
    }
  },
  {
    path: '/admin',
    name: 'AdminConsole',
    component: () => import('../views/AdminConsole.vue'),
    meta: { requiresAuth: true, allowedRoles: ['admin', 'superadmin'], requiresAdminSession: true }
  },
  {
    path: '/admin/mfa',
    name: 'AdminMfaSetup',
    component: () => import('../views/AdminMfaSetup.vue'),
    meta: { requiresAuth: true, allowedRoles: ['admin', 'superadmin'], requiresAdminSession: true }
  },
  {
    path: '/superadmin',
    name: 'SuperAdminConsole',
    component: () => import('../views/SuperAdminConsole.vue'),
    meta: { requiresAuth: true, allowedRoles: ['superadmin'], requiresAdminSession: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

function missingRouteConsents(to, userStore) {
  const requiredConsents = to.meta.requiredConsents || []
  const bypassRoles = to.meta.consentBypassRoles || []
  if (!requiredConsents.length || bypassRoles.includes(userStore.userRole)) return []
  return requiredConsents.filter(key => !userStore.hasConsent(key))
}

// Navigation guard for authentication & role-based access
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  
  // Wait for initial session restoration before deciding the route. Await the
  // actual restore promise (idempotent — shares main.js's in-flight call via the
  // store's profile-fetch dedupe) rather than polling with a fixed cap: a cold
  // Cloud Run /auth/me can take ~8-10s, and the old 5s cap wrongly bounced
  // authenticated users (e.g. a dealer refreshing /dealer) to /login. A safety
  // race caps the wait so navigation never hangs if the API stalls.
  if (!userStore.initialized) {
    console.log('[Router] Awaiting user store session restore...')
    const safety = new Promise((resolve) => setTimeout(resolve, 20000))
    await Promise.race([userStore.restoreSession().catch(() => {}), safety])
    console.log(`[Router] User store initialized: ${userStore.initialized}`)
  }

  // A fresh form login populates the user from the login payload without
  // consent state; unknown consent state must mean "fetch first", not
  // "missing", or every login re-prompts for consents already granted.
  if (userStore.isAuthenticated && !userStore.consentStatus && (to.meta.requiredConsents || []).length) {
    await userStore.getConsentStatus().catch(() => {})
  }
  const missingConsents = missingRouteConsents(to, userStore)
  const isAuthed = userStore.isAuthenticated
  // publicPreview routes stay fully gated for AUTHENTICATED users (consents,
  // roles) but render a read-only/indicative guest experience when logged out
  // instead of bouncing the visitor to /login.
  const allowsGuestPreview = to.meta.publicPreview === true

  if (to.meta.requiresAuth && !isAuthed && !allowsGuestPreview) {
    // Preserve the intended destination so login can complete the journey.
    next({ path: '/login', query: { redirect: to.fullPath } })
  } else if (to.meta.requiresAuth && isAuthed && !to.meta.allowsPendingConsent && !userStore.onboardingComplete) {
    next('/consent')
  } else if (to.name === 'ConsentOnboarding' && userStore.onboardingComplete && !to.query.required) {
    // Fully-onboarded users don't re-see generic onboarding — but when a route
    // guard sent them here to UNLOCK specific consents (?required=...), let the
    // page render (previously this bounced them to /dashboard and the consent
    // unlock flow was unreachable).
    next('/dashboard')
  } else if (to.meta.publicOnly && isAuthed) {
    next(userStore.onboardingComplete ? '/dashboard' : '/consent')
  } else if ((to.name === 'Login' || to.name === 'Signup') && isAuthed) {
    next(userStore.onboardingComplete ? '/dashboard' : '/consent')
  } else if (to.meta.allowedRoles && isAuthed && !roleIsOneOf(userStore.userRole, to.meta.allowedRoles)) {
    // Role-based guard: redirect to dashboard if user lacks permission
    next('/dashboard')
  } else if (to.meta.requiresAuth && isAuthed && missingConsents.length) {
    next({
      name: 'ConsentOnboarding',
      query: {
        next: to.fullPath,
        required: missingConsents.join(',')
      }
    })
  } else if (to.meta.requiresAdminSession && !userStore.isAdminAuthenticated) {
    next('/admin-login')
  } else {
    next()
  }
})

export default router
